// sim/genes.ts — the GeneInventory: a per-colony, Structure-of-Arrays store of
// gene "item objects", plus lineage, stacking, expression, and describe helpers.
//
// WHAT A "GENE" IS HERE (data-oriented mandate, DESIGN.md §2 / EFFICIENCY.md §B):
//   A gene is NOT a heap object. It is an integer id (GeneId) = a row across the
//   parallel typed-array columns below, reconstructable only via the accessors.
//   Thousands of genes cost a handful of flat arrays and zero per-gene GC churn —
//   exactly like organisms (EntitySystem) and colonies (ColonySystem).
//
// STACKING:
//   Traits are not booleans. Each gene carries a per-trait COUNT (`traitCount`),
//   so the same mutation can stack (Reinforced×3) and the count is the magnitude
//   multiplier at expression time. `traitTotal` caches the sum for cheap hashing.
//
// EFFECTS ARE ROUTED TO CHANNELS, NOT BARE STATS:
//   Expression never writes a raw stat. It accumulates into EFFECT_CHANNEL.* slots
//   (Movement / Combat / Sensing / Defense / Toxicity / Regen / Size). That array
//   IS the seam future BEHAVIOUR and RENDERING read — genes describe *which channel*
//   they modulate, decoupled from however a caste later consumes it.
//
// ALLOCATION DISCIPLINE:
//   insert / addTrait / expressGene / colonyGeneProfile allocate nothing per call.
//   Expression writes into a caller-owned out-param (length EFFECT_CHANNEL_COUNT).
//   A free-list is present for correctness/future reuse; in normal play genes only
//   accumulate (loot is permanent), so despawn is intentionally not wired into the
//   hot path.
//
// DETERMINISM:
//   Pure integer state (exact FNV hashing). The per-colony intrusive list is
//   walked head→next, i.e. in stable insertion order, so a hash over a colony's
//   genes is reproducible for a given seed + tick.

import { NIL, GENE_TRAIT_COUNT, RARITY, EFFECT_CHANNEL_COUNT } from '../types.ts';
import { softCapChannel } from './geneExpr.ts';
import type { GeneConfig } from '../types.ts';
import {
  GENE_TYPE_NAME,
  GENE_TYPE_GLYPH,
  GENE_TYPE_BASE_CHANNEL,
  TRAIT_NAME,
  TRAIT_CHANNEL,
  TRAIT_CHANNEL2,
  TRAIT_MAGNITUDE,
  TRAIT_MAGNITUDE2,
  RARITY_NAME,
  RARITY_GLYPH,
  RARITY_MAG_BONUS,
} from './geneData.ts';

/** A gene is an integer id = a row index into the GeneInventory columns. */
export type GeneId = number;

/** Short caste labels for describeGene's source field. Matches CASTE.* order. */
const CASTE_LABEL: readonly string[] = ['worker', 'soldier', 'flyer', 'specialist'];

/**
 * Per-colony store of gene "item objects". Total row capacity is
 * `maxColonies * config.perColonyCapacity`; ids are dense indices into the
 * columns. Each colony owns an intrusive singly-linked list of its gene ids
 * (head + next), giving O(1) push-front and a deterministic walk for iteration
 * and hashing.
 */
export class GeneInventory {
  readonly capacity: number;
  readonly maxColonies: number;
  private readonly perColonyCapacity: number;
  /** OPT-IN: evict a colony's lowest-rarity gene for a strictly-rarer drop. */
  private readonly evictWhenFull: boolean;

  // --- Gene columns (SoA). Index by GeneId. ---
  /** Base body-part type (GENE_TYPE.*). */
  readonly type: Uint8Array;
  /** Rarity tier (RARITY.*). */
  readonly rarity: Uint8Array;
  /** Owning colony id. */
  readonly owner: Int32Array;
  /** Monotonic unique serial (lineage identity; survives despawn/reuse). */
  readonly serial: Int32Array;
  /** Serial of the gene this one was spliced from (NIL for forged/leaf genes). */
  readonly parentSerial: Int32Array;
  /** Splice depth (0 for a freshly forged gene). */
  readonly generation: Int32Array;
  /** Colony the gene's lineage originated in (provenance, copied through splice). */
  readonly originColony: Int32Array;
  /** Caste the gene was looted from, or a negative sentinel for event drops. */
  readonly sourceCaste: Int32Array;
  /** Tick the gene entered the world. */
  readonly originTick: Int32Array;
  /** Cached sum of all trait counts (== Σ traitCount slice). */
  readonly traitTotal: Int32Array;
  /** 1 if the row is a live gene, 0 otherwise. */
  readonly alive: Uint8Array;
  /** STACKING store: count per trait, index [id * GENE_TRAIT_COUNT + trait]. */
  readonly traitCount: Int32Array;

  // --- Per-colony intrusive list (push-front, deterministic walk). ---
  /** Head GeneId of each colony's list, or NIL. Index by colony id. */
  private readonly head: Int32Array;
  /** Next GeneId in the owner's list, or NIL. Index by GeneId. */
  private readonly next: Int32Array;
  /** Live gene count per colony. Index by colony id. */
  private readonly countByColony: Int32Array;

  // --- Free-list allocator (LIFO; present for correctness/future reuse). ---
  private readonly freeList: Int32Array;
  private freeCount: number;
  /** Next never-yet-allocated id (high-water mark). */
  private highWater: number;
  /** Monotonic serial source — never reset, so lineage ids stay unique. */
  private serialCounter: number;

  // --- Public counters (deterministic; tests read them). ---
  /** Genes produced by the forge (procedural generator). */
  forgedTotal = 0;
  /** Drops awarded to a killer from a slain rival. */
  combatDropsTotal = 0;
  /** Salvage drops to a colony from its own naturally-dead members. */
  deathDropsTotal = 0;
  /** Genes granted through the generic event entry point. */
  eventDropsTotal = 0;
  /** Splices performed (lineage + stacking). */
  spliceTotal = 0;
  /** Inserts rejected because a colony was at capacity (or no slot free). */
  droppedFullTotal = 0;
  /** Genes evicted to make room for a strictly-rarer drop (opt-in policy). */
  evictedTotal = 0;

  constructor(maxColonies: number, config: GeneConfig) {
    if (maxColonies <= 0) throw new Error('GeneInventory maxColonies must be > 0');
    if (config.perColonyCapacity <= 0)
      throw new Error('GeneInventory perColonyCapacity must be > 0');

    this.maxColonies = maxColonies;
    this.perColonyCapacity = config.perColonyCapacity;
    this.evictWhenFull = config.evictWhenFull === true;
    const capacity = maxColonies * config.perColonyCapacity;
    this.capacity = capacity;

    this.type = new Uint8Array(capacity);
    this.rarity = new Uint8Array(capacity);
    this.owner = new Int32Array(capacity).fill(NIL);
    this.serial = new Int32Array(capacity).fill(NIL);
    this.parentSerial = new Int32Array(capacity).fill(NIL);
    this.generation = new Int32Array(capacity);
    this.originColony = new Int32Array(capacity).fill(NIL);
    this.sourceCaste = new Int32Array(capacity).fill(NIL);
    this.originTick = new Int32Array(capacity);
    this.traitTotal = new Int32Array(capacity);
    this.alive = new Uint8Array(capacity);
    this.traitCount = new Int32Array(capacity * GENE_TRAIT_COUNT);

    this.head = new Int32Array(maxColonies).fill(NIL);
    this.next = new Int32Array(capacity).fill(NIL);
    this.countByColony = new Int32Array(maxColonies);

    this.freeList = new Int32Array(capacity);
    this.freeCount = 0;
    this.highWater = 0;
    this.serialCounter = 0;
  }

  // --- Allocation ------------------------------------------------------------

  /**
   * Create a leaf gene for `owner`. Returns its GeneId, or NIL if the colony is
   * already at `perColonyCapacity` OR no row slot is free. The per-colony cap is
   * checked FIRST so a full colony never consumes a slot. Zeroes the trait slice
   * and pushes the new id onto the front of the owner's list. O(1),
   * allocation-free.
   */
  insert(
    owner: number,
    type: number,
    rarity: number,
    originColony: number,
    sourceCaste: number,
    originTick: number,
  ): GeneId {
    // Per-colony cap first — do NOT consume a slot for a rejected insert.
    if ((this.countByColony[owner] as number) >= this.perColonyCapacity) {
      // OPT-IN eviction seam: a strictly-rarer newcomer bumps the colony's
      // lowest-rarity gene (ties → the oldest in list order). retire() frees the
      // slot + drops the count, so the allocation below then succeeds. The
      // default (flag off) falls straight through to the historical discard path.
      if (this.evictWhenFull) {
        const victim = this.lowestRarityGene(owner);
        if (victim !== NIL && rarity > (this.rarity[victim] as number)) {
          this.retire(victim);
          this.evictedTotal++;
          // count is now below cap; continue to the normal allocation below.
        } else {
          this.droppedFullTotal++;
          return NIL;
        }
      } else {
        this.droppedFullTotal++;
        return NIL;
      }
    }

    let id: GeneId;
    if (this.freeCount > 0) {
      id = this.freeList[--this.freeCount] as number;
    } else if (this.highWater < this.capacity) {
      id = this.highWater++;
    } else {
      // No row available anywhere (global capacity exhausted).
      this.droppedFullTotal++;
      return NIL;
    }

    this.type[id] = type;
    this.rarity[id] = rarity;
    this.owner[id] = owner;
    this.serial[id] = this.serialCounter++;
    this.parentSerial[id] = NIL;
    this.generation[id] = 0;
    this.originColony[id] = originColony;
    this.sourceCaste[id] = sourceCaste;
    this.originTick[id] = originTick;
    this.traitTotal[id] = 0;
    this.alive[id] = 1;

    // Zero this gene's trait slice (stacking starts empty).
    const base = id * GENE_TRAIT_COUNT;
    for (let t = 0; t < GENE_TRAIT_COUNT; t++) this.traitCount[base + t] = 0;

    // Push-front onto the owner's intrusive list.
    this.next[id] = this.head[owner] as number;
    this.head[owner] = id;
    this.countByColony[owner] = (this.countByColony[owner] as number) + 1;

    return id;
  }

  /**
   * The lowest-rarity gene `owner` holds, breaking ties toward the OLDEST in
   * deterministic list order, or NIL if the colony holds none. The intrusive
   * list is push-front (newest→oldest under firstOf→nextOf), so taking the
   * candidate on `<=` lets a later (older) equal-rarity node win the tie. Used by
   * the opt-in full-inventory eviction policy; O(list length), off the hot path.
   * Allocation-free + deterministic.
   */
  private lowestRarityGene(owner: number): GeneId {
    let best: GeneId = NIL;
    let bestRarity = Number.MAX_SAFE_INTEGER;
    for (let id = this.head[owner] as number; id !== NIL; id = this.next[id] as number) {
      const r = this.rarity[id] as number;
      if (r <= bestRarity) {
        bestRarity = r;
        best = id;
      }
    }
    return best;
  }

  /**
   * Stacking primitive: add `n` stacks of `trait` to gene `id`. Bumps the cached
   * `traitTotal`. Allocation-free.
   */
  addTrait(id: GeneId, trait: number, n = 1): void {
    this.traitCount[id * GENE_TRAIT_COUNT + trait] =
      (this.traitCount[id * GENE_TRAIT_COUNT + trait] as number) + n;
    this.traitTotal[id] = (this.traitTotal[id] as number) + n;
  }

  // --- Removal (the long-deferred eviction seam) ----------------------------

  /**
   * Retire a live gene: unlink it from its owner's intrusive list (scan head→
   * predecessor, then splice), mark it dead, drop the colony count, and push the
   * row onto the free-list so the NEXT `insert` reuses the slot. No-op if the
   * gene is already dead. O(list length) — used off the hot path (the sacrifice
   * cadence), never per tick. This is the gene-eviction seam noted in
   * docs/BACKLOG.md; until now the free-list existed but was never populated.
   * Allocation-free + deterministic.
   */
  retire(id: GeneId): void {
    if (this.alive[id] === 0) return;
    const owner = this.owner[id] as number;

    // Unlink from the owner's singly-linked list (push-front order).
    const head = this.head[owner] as number;
    if (head === id) {
      this.head[owner] = this.next[id] as number;
    } else {
      let prev = head;
      // Walk to the node whose next is `id`. `prev` is always live here.
      while (prev !== NIL && (this.next[prev] as number) !== id) {
        prev = this.next[prev] as number;
      }
      if (prev !== NIL) this.next[prev] = this.next[id] as number;
    }
    this.next[id] = NIL;

    // Mark dead, drop the per-colony count, recycle the slot via the free-list.
    this.alive[id] = 0;
    this.owner[id] = NIL;
    const c = this.countByColony[owner] as number;
    if (c > 0) this.countByColony[owner] = c - 1;
    this.freeList[this.freeCount++] = id;
  }

  /**
   * Raise gene `id`'s rarity to max(current, minRarity). Used by the box system's
   * pity guarantee to FLOOR a bonus gene at "rare". Traits are untouched (only the
   * rarity tier changes). Allocation-free; no-op when already at/above minRarity.
   */
  promoteToRarity(id: GeneId, minRarity: number): void {
    if ((this.rarity[id] as number) < minRarity) this.rarity[id] = minRarity;
  }

  /**
   * SET gene `id`'s rarity to an exact tier (clamped to [0, RARITY.Legendary]). Unlike
   * `promoteToRarity` this can also DEMOTE — the upgrade-pacing layer (src/sim/upgradePacing.ts)
   * forces a reward's final rarity to a paced tier, which may be lower than the forge rolled, so a
   * controlled magnitude distribution holds. Traits are untouched (only the rarity tier changes);
   * the relative-loot fitter re-stacks the gene to the new rarity's power window afterward.
   * Allocation-free.
   */
  setRarity(id: GeneId, rarity: number): void {
    const r = rarity < 0 ? 0 : rarity > RARITY.Legendary ? RARITY.Legendary : rarity | 0;
    this.rarity[id] = r;
  }

  // --- Accessors -------------------------------------------------------------

  typeOf(id: GeneId): number {
    return this.type[id] as number;
  }
  rarityOf(id: GeneId): number {
    return this.rarity[id] as number;
  }
  ownerOf(id: GeneId): number {
    return this.owner[id] as number;
  }
  serialOf(id: GeneId): number {
    return this.serial[id] as number;
  }
  parentSerialOf(id: GeneId): number {
    return this.parentSerial[id] as number;
  }
  generationOf(id: GeneId): number {
    return this.generation[id] as number;
  }
  originColonyOf(id: GeneId): number {
    return this.originColony[id] as number;
  }
  sourceCasteOf(id: GeneId): number {
    return this.sourceCaste[id] as number;
  }
  originTickOf(id: GeneId): number {
    return this.originTick[id] as number;
  }
  /** Stack count of `trait` on gene `id`. */
  traitOf(id: GeneId, trait: number): number {
    return this.traitCount[id * GENE_TRAIT_COUNT + trait] as number;
  }
  traitTotalOf(id: GeneId): number {
    return this.traitTotal[id] as number;
  }
  isAlive(id: GeneId): boolean {
    return this.alive[id] === 1;
  }

  // --- Per-colony iteration (deterministic insertion order) -----------------

  /** First (most recently inserted) GeneId of `colony`'s list, or NIL. */
  firstOf(colony: number): GeneId {
    return this.head[colony] as number;
  }
  /** Next GeneId after `id` in its owner's list, or NIL. */
  nextOf(id: GeneId): GeneId {
    return this.next[id] as number;
  }
  /** Live gene count held by `colony`. */
  countOf(colony: number): number {
    return this.countByColony[colony] as number;
  }

  // --- Lineage + stacking demonstrator --------------------------------------

  /**
   * Splice two same-owner genes into a child that INHERITS lineage and STACKS
   * both parents' traits. Child type/origin come from `a`; rarity is one tier
   * above the better parent (capped at Legendary). Throws if the parents have
   * different owners. Returns NIL if the owner is at capacity. spliceTotal++.
   */
  spliceGenes(a: GeneId, b: GeneId): GeneId {
    const owner = this.ownerOf(a);
    if (owner !== this.ownerOf(b)) {
      throw new Error(`spliceGenes: owner mismatch (${owner} !== ${this.ownerOf(b)})`);
    }
    const ra = this.rarityOf(a);
    const rb = this.rarityOf(b);
    const childRarity = Math.min((ra > rb ? ra : rb) + 1, RARITY.Legendary);

    const child = this.insert(
      owner,
      this.typeOf(a),
      childRarity,
      this.originColonyOf(a),
      this.sourceCasteOf(a),
      this.originTickOf(a),
    );
    if (child === NIL) return NIL;

    this.parentSerial[child] = this.serialOf(a);
    const ga = this.generationOf(a);
    const gb = this.generationOf(b);
    this.generation[child] = (ga > gb ? ga : gb) + 1;

    // Stack BOTH parents' trait counts into the child.
    for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
      const sum = this.traitOf(a, t) + this.traitOf(b, t);
      if (sum !== 0) this.addTrait(child, t, sum);
    }

    this.spliceTotal++;
    return child;
  }

  // --- Expression (the behaviour/visual SEAM) -------------------------------

  /**
   * Express gene `id` into `out` (length EFFECT_CHANNEL_COUNT, caller-owned).
   * Routes the gene's effect onto EFFECT_CHANNEL.* slots — never a raw stat:
   *   - base: +(1 + rarity) on the type's base channel;
   *   - each trait t with count c>0: +c*(magnitude + rarity bonus) on its channel,
   *     plus a secondary tradeoff (c * magnitude2, no rarity bonus) when set.
   * Sums accumulate as honest integers, then a per-channel SOFT CAP
   * (`softCapChannel`) bounds runaway magnitudes: every normal/small value passes
   * through UNCHANGED (the cap only bites well above the threshold), so stacking
   * stays directly testable while a pathological deeply-spliced gene can no longer
   * express into the thousands. Allocation-free.
   */
  expressGene(id: GeneId, out: Int32Array): void {
    out.fill(0);

    const rarity = this.rarityOf(id);
    const baseChannel = GENE_TYPE_BASE_CHANNEL[this.typeOf(id)] as number;
    out[baseChannel] = (out[baseChannel] as number) + 1 + rarity;

    const ragBonus = RARITY_MAG_BONUS[rarity] as number;
    const base = id * GENE_TRAIT_COUNT;
    for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
      const c = this.traitCount[base + t] as number;
      if (c <= 0) continue;
      const ch = TRAIT_CHANNEL[t] as number;
      out[ch] = (out[ch] as number) + c * ((TRAIT_MAGNITUDE[t] as number) + ragBonus);
      const ch2 = TRAIT_CHANNEL2[t] as number;
      if (ch2 >= 0) {
        out[ch2] = (out[ch2] as number) + c * (TRAIT_MAGNITUDE2[t] as number);
      }
    }

    // Soft-cap each channel in place: diminishing returns past the threshold,
    // hard-clamped at the ceiling. A no-op for all normal magnitudes.
    for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) {
      out[c] = softCapChannel(out[c] as number);
    }
  }

  /**
   * Sum the expression of every gene a colony holds into `out` (length
   * EFFECT_CHANNEL_COUNT). `scratch` (same length) is reused per gene so the walk
   * allocates nothing. Walk order is insertion order (deterministic).
   */
  colonyGeneProfile(colony: number, out: Int32Array, scratch: Int32Array): void {
    out.fill(0);
    for (let id = this.firstOf(colony); id !== NIL; id = this.nextOf(id)) {
      this.expressGene(id, scratch);
      for (let c = 0; c < EFFECT_CHANNEL_COUNT; c++) {
        out[c] = (out[c] as number) + (scratch[c] as number);
      }
    }
  }

  // --- Visual / describe -----------------------------------------------------

  /** Type glyph for gene `id`, uppercased once rarity ≥ Rare (visual prominence). */
  geneGlyph(id: GeneId): string {
    const g = GENE_TYPE_GLYPH[this.typeOf(id)] as string;
    return this.rarityOf(id) >= RARITY.Rare ? g.toUpperCase() : g;
  }

  /**
   * Human-readable one-liner, e.g.
   *   "[Rare *] Reinforced×2 Toxic Mandible (gen1 ⟵ soldier@c0 t37)"
   * Shows the rarity name+glyph, each present trait (with ×N when stacked >1),
   * the type name, splice generation, and provenance (source caste or `event`,
   * origin colony, origin tick). For debug/CLI only — not a hot path.
   */
  describeGene(id: GeneId): string {
    const rarity = this.rarityOf(id);
    let s = `[${RARITY_NAME[rarity] as string} ${RARITY_GLYPH[rarity] as string}] `;

    for (let t = 0; t < GENE_TRAIT_COUNT; t++) {
      const c = this.traitOf(id, t);
      if (c <= 0) continue;
      s += (TRAIT_NAME[t] as string) + (c > 1 ? `×${c}` : '') + ' ';
    }

    s += GENE_TYPE_NAME[this.typeOf(id)] as string;

    const src = this.sourceCasteOf(id);
    const srcLabel = src < 0 ? 'event' : ((CASTE_LABEL[src] as string | undefined) ?? `caste${src}`);
    s += ` (gen${this.generationOf(id)} ⟵ ${srcLabel}@c${this.originColonyOf(id)} t${this.originTickOf(id)})`;
    return s;
  }
}
